import { Seo } from '@frontend/shared/web';
import { useSearchParams } from 'react-router-dom';

import { AdminTabs } from '../../components/admin/AdminTabs';
import { InvitationsTab } from '../../components/admin/InvitationsTab';
import { MembersTab } from '../../components/admin/MembersTab';
import { Header } from '../../components/layout/Header';
import { useAccount } from '../../contexts/AccountContext';

const MEMBERS_TAB = { key: 'members', label: 'Membres' } as const;
const INVITATIONS_TAB = { key: 'invitations', label: 'Invitations' } as const;

type AdminTab = typeof MEMBERS_TAB.key | typeof INVITATIONS_TAB.key;

/**
 * Écran « Droits et accès ». Deux formes selon l'appelant (cf.
 * `RequireAdminArea`) : un `extranet-admin` gère les membres et les
 * invitations ; tout autre membre n'y trouve que les invitations, sur ses
 * propres rôles et périmètres.
 */
export const AdminRoute = () => {
  const { isAdmin } = useAccount();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = isAdmin ? ([MEMBERS_TAB, INVITATIONS_TAB] as const) : ([INVITATIONS_TAB] as const);
  const rawTab = searchParams.get('tab');
  // Un `?tab=members` forgé ne doit pas ouvrir l'onglet aux non-admins.
  const tab: AdminTab = tabs.some(t => t.key === rawTab) ? (rawTab as AdminTab) : tabs[0].key;

  // Changer d'onglet repart d'une pagination et d'un filtre vierges.
  const setTab = (next: AdminTab) => setSearchParams({ tab: next });

  const title = isAdmin ? 'Administration' : 'Invitations';

  return (
    <>
      <Seo title={`${title} — Portail extranet`} />
      <Header
        title={title}
        subtitle={
          isAdmin
            ? 'Membres du portail, rôles, périmètres et invitations'
            : 'Inviter un membre sur vos propres rôles et périmètres'
        }
      />

      <div className="flex-1 overflow-y-auto">
        <div className="px-8 py-6 flex flex-col gap-5">
          {tabs.length > 1 ? <AdminTabs value={tab} onChange={setTab} items={tabs} /> : null}
          {tab === 'members' ? <MembersTab /> : <InvitationsTab />}
        </div>
      </div>
    </>
  );
};
